
import { defineComponent, ref, computed, onMounted, watch, onBeforeUnmount } from 'vue'
import * as echarts from 'echarts'
import type { ECharts } from 'echarts'
import type { PositionDistribution } from '@/types/arkOne'
import Nodata from '@/components/page/nodata.vue'

const __default__ = defineComponent({
  name: 'PositionDistribution',
  components: {
    Nodata
  },
  props: {
    positions: {
      type: Array as () => PositionDistribution[],
      required: true
    },
    theme: {
      type: String,
      default: 'dark'
    },
    language: {
      type: String,
      default: 'zh-Hans'
    }
  },
  setup(props) {
    const chartRef = ref<HTMLElement | null>(null)
    let chartInstance: ECharts | null = null

    const isTraditional = computed(() => props.language === 'zh-Hant')

    const t = (key: string): string => {
      const translations: Record<string, Record<string, string>> = {
        'positionDistribution': {
          'title': isTraditional.value ? '持倉分佈' : '持仓分布',
          'other': isTraditional.value ? '其他' : '其他',
          'noData': isTraditional.value ? '暫無持倉分佈數據' : '暂无持仓分布数据'
        }
      }
      const [category, subKey] = key.split('.')
      return translations[category]?.[subKey] || key
    }

    const colors = [
      '#1890ff',
      '#52c41a',
      '#faad14',
      '#f5222d',
      '#722ed1',
      '#999999'
    ]

    // 最多6条：前5名具体持仓 + 其他
    const displayData = computed(() => {
      const sorted = props.positions.slice().sort((a, b) => b.percentage - a.percentage)

      const isOther = (item: PositionDistribution) => {
        const code = (item.code || '').toString().toUpperCase()
        return code === 'OTHER' || code === 'OTHERS' || code === 'others' || /其他/i.test(item.name || '')
      }

      const otherItems = sorted.filter(isOther)
      const normalItems = sorted.filter(item => !isOther(item))

      const top5 = normalItems.slice(0, 5)
      const overflow = normalItems.slice(5)

      const othersSum =
        otherItems.reduce((sum, item) => sum + item.percentage, 0) +
        overflow.reduce((sum, item) => sum + item.percentage, 0)

      if (othersSum > 0) {
        return top5.concat([{ name: t('positionDistribution.other'), code: 'OTHER', percentage: othersSum }])
      }
      return top5
    })

    const formatValue = (value: number): string => {
      return `${value.toFixed(2)}%`
    }

    const initChart = () => {
      if (!chartRef.value) return
      chartInstance = echarts.init(chartRef.value)
      updateChart()
    }

    const updateChart = () => {
      if (!chartInstance) return

      const chartData = displayData.value.map((item, index) => ({
        name: item.name,
        value: item.percentage,
        itemStyle: { color: colors[index] }
      }))

      const option: echarts.EChartsOption = {
        backgroundColor: 'transparent',
        tooltip: {
          trigger: 'item',
          confine: true,
          appendToBody: true,
          formatter: (params: any) => {
            return `${params.name}: ${params.value.toFixed(2)}%`
          }
        },
        series: [
          {
            type: 'pie',
            radius: ['40%', '70%'],
            center: ['50%', '50%'],
            avoidLabelOverlap: false,
            label: { show: false },
            labelLine: { show: false },
            data: chartData
          }
        ]
      }

      chartInstance.setOption(option)
    }

    onMounted(() => {
      initChart()
      window.addEventListener('resize', () => {
        chartInstance?.resize()
      })
    })

    onBeforeUnmount(() => {
      chartInstance?.dispose()
    })

    watch(() => props.positions, () => {
      updateChart()
    }, { deep: true })

    return {
      chartRef,
      displayData,
      colors,
      formatValue,
      t
    }
  }
})

import { useCssVars as _useCssVars } from 'vue'
const __injectCSSVars__ = () => {
_useCssVars(_ctx => ({
  "d6b747fc": (_ctx.theme === "light" ? "#ffffff" : "#1A1D2F"),
  "7fe7b5ce": (_ctx.theme === "light" ? "#000000" : "#ffffff"),
  "37f7a6e9": (_ctx.theme === "light" ? "#333333" : "#e5e5e5")
}))}
const __setup__ = __default__.setup
__default__.setup = __setup__
  ? (props, ctx) => { __injectCSSVars__();return __setup__(props, ctx) }
  : __injectCSSVars__

export default __default__